(() => {
  const sectionSelector = '[data-glossaire]';
  const viewportSelector = '[data-glossaire-viewport]';
  const trackSelector = '[data-glossaire-track]';
  const tabSelector = '[data-glossaire-tab]';
  const groupSelector = '[data-glossaire-group]';
  const scrollbarSelector = '[data-glossaire-scrollbar]';
  const thumbSelector = '[data-glossaire-thumb]';
  const initialized = 'glossaireInitialized';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const thumbWidthPercent = 28.21;
  const viewportDragThreshold = 5;

  const updateThumb = (viewport, thumb) => {
    if (!thumb) return;

    const scrollbar = thumb.parentElement;
    const maxScroll = viewport.scrollWidth - viewport.clientWidth;
    const scrollbarWidth = scrollbar ? scrollbar.clientWidth : 0;

    if (scrollbar?.classList.contains('fwn-slider')) {
      window.FWNSliders?.setPosition(scrollbar, viewport.scrollLeft, maxScroll);
      return;
    }

    const thumbWidthPixels = scrollbarWidth * (thumbWidthPercent / 100);
    const maxOffset = Math.max(scrollbarWidth - thumbWidthPixels, 0);
    const offset = maxScroll > 0 ? (viewport.scrollLeft / maxScroll) * maxOffset : 0;

    thumb.style.width = `${thumbWidthPercent}%`;
    thumb.style.transform = `translateX(${offset}px)`;
  };

  const getGroupLeft = (viewport, group, track) => {
    const viewportInset = Number.parseFloat(window.getComputedStyle(viewport).paddingInlineStart) || 0;

    return Math.max(group.offsetLeft - track.offsetLeft - viewportInset, 0);
  };

  const getClosestGroup = (viewport, track, groups) => {
    let closestGroup = groups[0];
    let closestDistance = Number.POSITIVE_INFINITY;

    groups.forEach((group) => {
      const distance = Math.abs(viewport.scrollLeft - getGroupLeft(viewport, group, track));

      if (distance < closestDistance) {
        closestDistance = distance;
        closestGroup = group;
      }
    });

    return closestGroup;
  };

  const updateTabs = (tabs, activeKey) => {
    let activeTab = null;

    tabs.forEach((tab) => {
      const isActive = tab.dataset.glossaireTab === activeKey;

      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      if (isActive) activeTab = tab;
    });

    if (!activeTab) return;

    const tabsViewport = activeTab.parentElement;
    const tabLeft = activeTab.offsetLeft;
    const tabRight = tabLeft + activeTab.offsetWidth;
    const visibleLeft = tabsViewport.scrollLeft;
    const visibleRight = visibleLeft + tabsViewport.clientWidth;

    if (tabLeft < visibleLeft || tabRight > visibleRight) {
      tabsViewport.scrollTo({
        left: tabLeft - (tabsViewport.clientWidth - activeTab.offsetWidth) / 2,
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    }
  };

  const initSection = (section) => {
    if (!section || section.dataset[initialized] === 'true') return;

    const viewport = section.querySelector(viewportSelector);
    const track = section.querySelector(trackSelector);
    const scrollbar = section.querySelector(scrollbarSelector);
    const thumb = section.querySelector(thumbSelector);
    const tabs = Array.from(section.querySelectorAll(tabSelector));
    const groups = Array.from(section.querySelectorAll(groupSelector));

    if (!viewport || !track || !groups.length) return;

    section.dataset[initialized] = 'true';

    let frame = 0;
    let dragFrame = 0;
    let dragPointerId = null;
    let dragOffset = 0;
    let pendingScrollLeft = 0;
    let viewportDragFrame = 0;
    let viewportDragPointerId = null;
    let viewportDragStartX = 0;
    let viewportDragStartScrollLeft = 0;
    let viewportDragMoved = false;
    let pendingViewportScrollLeft = 0;
    let suppressViewportClick = false;
    let resizeObserver = null;
    const tabHandlers = new Map();

    const setScrollFromPointer = (clientX) => {
      if (!scrollbar || !thumb) return;

      const maxScroll = viewport.scrollWidth - viewport.clientWidth;
      const scrollbarRect = scrollbar.getBoundingClientRect();
      const thumbWidth = thumb.getBoundingClientRect().width;
      const maxThumbOffset = Math.max(scrollbarRect.width - thumbWidth, 0);
      const startOffset = window.FWNSliders?.getStartOffset(scrollbar, scrollbarRect.width, maxThumbOffset) || 0;
      const availableOffset = Math.max(maxThumbOffset - startOffset, 0);

      if (maxScroll <= 0 || availableOffset <= 0) return;

      const thumbOffset = Math.min(
        Math.max(clientX - scrollbarRect.left - dragOffset, startOffset),
        maxThumbOffset
      );

      pendingScrollLeft = ((thumbOffset - startOffset) / availableOffset) * maxScroll;

      if (dragFrame) return;

      dragFrame = window.requestAnimationFrame(() => {
        dragFrame = 0;
        viewport.scrollLeft = pendingScrollLeft;
      });
    };

    const handleScrollbarPointerDown = (event) => {
      if (!scrollbar || !thumb || event.button !== 0) return;

      const maxScroll = viewport.scrollWidth - viewport.clientWidth;
      if (maxScroll <= 0) return;

      const thumbRect = thumb.getBoundingClientRect();
      const clickedThumb = event.target === thumb;

      event.preventDefault();
      dragPointerId = event.pointerId;
      dragOffset = clickedThumb ? event.clientX - thumbRect.left : thumbRect.width / 2;
      scrollbar.classList.add('is-dragging');
      scrollbar.setPointerCapture(event.pointerId);
      setScrollFromPointer(event.clientX);
    };

    const handleScrollbarPointerMove = (event) => {
      if (event.pointerId !== dragPointerId) return;
      setScrollFromPointer(event.clientX);
    };

    const stopScrollbarDrag = (event) => {
      if (!scrollbar || event.pointerId !== dragPointerId) return;

      if (scrollbar.hasPointerCapture(event.pointerId)) {
        scrollbar.releasePointerCapture(event.pointerId);
      }

      dragPointerId = null;
      scrollbar.classList.remove('is-dragging');
    };

    const setViewportScrollFromPointer = (clientX) => {
      const maxScroll = viewport.scrollWidth - viewport.clientWidth;
      const deltaX = clientX - viewportDragStartX;

      pendingViewportScrollLeft = Math.min(
        Math.max(viewportDragStartScrollLeft - deltaX, 0),
        maxScroll
      );

      if (viewportDragFrame) return;

      viewportDragFrame = window.requestAnimationFrame(() => {
        viewportDragFrame = 0;
        viewport.scrollLeft = pendingViewportScrollLeft;
      });
    };

    const handleViewportPointerDown = (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (viewport.scrollWidth <= viewport.clientWidth) return;

      viewportDragPointerId = event.pointerId;
      viewportDragStartX = event.clientX;
      viewportDragStartScrollLeft = viewport.scrollLeft;
      viewportDragMoved = false;
      suppressViewportClick = false;
    };

    const handleViewportPointerMove = (event) => {
      if (event.pointerId !== viewportDragPointerId) return;

      const deltaX = event.clientX - viewportDragStartX;

      if (!viewportDragMoved && Math.abs(deltaX) < viewportDragThreshold) return;

      event.preventDefault();
      if (!viewportDragMoved && viewport.setPointerCapture) {
        viewport.setPointerCapture(event.pointerId);
      }
      viewportDragMoved = true;
      viewport.classList.add('is-dragging');
      setViewportScrollFromPointer(event.clientX);
    };

    const stopViewportDrag = (event) => {
      if (event.pointerId !== viewportDragPointerId) return;

      if (viewport.hasPointerCapture(event.pointerId)) {
        viewport.releasePointerCapture(event.pointerId);
      }

      viewportDragPointerId = null;
      viewport.classList.remove('is-dragging');

      if (!viewportDragMoved) return;

      if (viewportDragFrame) {
        window.cancelAnimationFrame(viewportDragFrame);
        viewportDragFrame = 0;
        viewport.scrollLeft = pendingViewportScrollLeft;
      }

      suppressViewportClick = true;
      const activeGroup = getClosestGroup(viewport, track, groups);

      if (activeGroup) {
        viewport.scrollTo({
          left: getGroupLeft(viewport, activeGroup, track),
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      }

      window.setTimeout(() => {
        suppressViewportClick = false;
      }, 0);
    };

    const handleViewportClick = (event) => {
      if (!suppressViewportClick) return;

      event.preventDefault();
      event.stopPropagation();
      suppressViewportClick = false;
    };

    const preventNativeDrag = (event) => event.preventDefault();

    const scheduleUpdate = () => {
      if (frame) return;

      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const activeGroup = getClosestGroup(viewport, track, groups);

        if (activeGroup) updateTabs(tabs, activeGroup.dataset.glossaireGroup);
        updateThumb(viewport, thumb);
      });
    };

    tabs.forEach((tab) => {
      const handleTabClick = () => {
        if (tab.disabled) return;

        const group = section.querySelector(`[data-glossaire-group="${tab.dataset.glossaireTab}"]`);

        if (!group) return;

        updateTabs(tabs, tab.dataset.glossaireTab);

        viewport.scrollTo({
          left: getGroupLeft(viewport, group, track),
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      };

      tabHandlers.set(tab, handleTabClick);
      tab.addEventListener('click', handleTabClick);
    });

    viewport.addEventListener('scroll', scheduleUpdate, { passive: true });
    viewport.addEventListener('pointerdown', handleViewportPointerDown);
    viewport.addEventListener('pointermove', handleViewportPointerMove);
    viewport.addEventListener('pointerup', stopViewportDrag);
    viewport.addEventListener('pointercancel', stopViewportDrag);
    viewport.addEventListener('click', handleViewportClick, true);
    viewport.addEventListener('dragstart', preventNativeDrag);
    scrollbar?.addEventListener('pointerdown', handleScrollbarPointerDown);
    scrollbar?.addEventListener('pointermove', handleScrollbarPointerMove);
    scrollbar?.addEventListener('pointerup', stopScrollbarDrag);
    scrollbar?.addEventListener('pointercancel', stopScrollbarDrag);

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      resizeObserver.observe(viewport);
    } else {
      window.addEventListener('resize', scheduleUpdate, { passive: true });
    }

    section.glossaireCleanup = () => {
      if (frame) window.cancelAnimationFrame(frame);
      if (dragFrame) window.cancelAnimationFrame(dragFrame);
      if (viewportDragFrame) window.cancelAnimationFrame(viewportDragFrame);
      tabHandlers.forEach((handler, tab) => tab.removeEventListener('click', handler));
      viewport.removeEventListener('scroll', scheduleUpdate);
      viewport.removeEventListener('pointerdown', handleViewportPointerDown);
      viewport.removeEventListener('pointermove', handleViewportPointerMove);
      viewport.removeEventListener('pointerup', stopViewportDrag);
      viewport.removeEventListener('pointercancel', stopViewportDrag);
      viewport.removeEventListener('click', handleViewportClick, true);
      viewport.removeEventListener('dragstart', preventNativeDrag);
      scrollbar?.removeEventListener('pointerdown', handleScrollbarPointerDown);
      scrollbar?.removeEventListener('pointermove', handleScrollbarPointerMove);
      scrollbar?.removeEventListener('pointerup', stopScrollbarDrag);
      scrollbar?.removeEventListener('pointercancel', stopScrollbarDrag);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
      delete section.dataset[initialized];
    };

    scheduleUpdate();
  };

  const initGlossaires = (root = document) => {
    if (root.matches?.(sectionSelector)) initSection(root);
    root.querySelectorAll?.(sectionSelector).forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initGlossaires(), { once: true });
  } else {
    initGlossaires();
  }

  document.addEventListener('shopify:section:load', (event) => initGlossaires(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.(sectionSelector)
      ? event.target
      : event.target.querySelector?.(sectionSelector);
    section?.glossaireCleanup?.();
  });
})();
